"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Menu, X, Smartphone } from "lucide-react";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const isBlogPage = pathname?.startsWith("/blog");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = pathname === "/" || pathname === "";
  
  const navigation = [
    { name: "Home", href: "/" },
    { name: "About Us", href: isHomePage ? "#about" : "/#about" },
    { name: "Services", href: isHomePage ? "#services" : "/#services" },
    { name: "Portfolio", href: isHomePage ? "#portfolio" : "/#portfolio" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: isHomePage ? "#contact" : "/#contact" },
  ];

  // Always show white background on blog pages or when scrolled to avoid visibility issues
  // On homepage, use semi-transparent white background even when not scrolled (hero has light background)
  const shouldShowWhiteBg = isBlogPage || isScrolled || isHomePage;
  const headerBg = shouldShowWhiteBg
    ? isScrolled || isBlogPage
      ? "bg-white/95 backdrop-blur-sm shadow-lg"
      : "bg-white/80 backdrop-blur-sm" // Lighter background on homepage when not scrolled
    : "bg-transparent";

  // Text color: always dark when we have a background (white or light), white only when truly transparent on dark backgrounds
  const textColor = shouldShowWhiteBg
    ? "text-secondary-700 hover:text-primary-600"
    : "text-white hover:text-primary-100";
  const logoTextColor = shouldShowWhiteBg ? "text-secondary-900" : "text-white";

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${headerBg}`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-600 rounded-lg">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <span
              className={`text-xl font-bold ${logoTextColor} transition-colors duration-200`}
            >
              Applanta
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`${textColor} font-medium transition-colors duration-200`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className={`md:hidden border-t shadow-lg transition-colors duration-200 ${
              shouldShowWhiteBg
                ? "bg-white border-secondary-200"
                : "bg-white/95 backdrop-blur-sm border-white/20"
            }`}
          >
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block text-secondary-700 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
